import { GlassCard } from '@/components/design/GlassCard';
import { NeonButton } from '@/components/design/NeonButton';
import { Target, Lock, Play, Settings, Plus } from 'lucide-react';

const LEVELS = [
  { id: 1, name: 'The Beginning', difficulty: 'Easy', status: 'Active', scenarios: 12 },
  { id: 2, name: 'Corporate Ladder', difficulty: 'Medium', status: 'Active', scenarios: 15 },
  { id: 3, name: 'Political Summit', difficulty: 'Hard', status: 'Locked', scenarios: 20 },
];

export const MazeManagerPage = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
         <div>
          <h2 className="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">Maze Manager</h2>
          <p className="text-muted-foreground">Configure game levels and scenarios.</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {LEVELS.map((level) => (
          <GlassCard key={level.id} hoverEffect className="relative overflow-hidden">
            <div className="flex justify-between items-start mb-4">
              <div className={`p-3 rounded-xl ${
                level.status === 'Locked' ? 'bg-white/5 text-muted-foreground' : 'bg-primary/20 text-primary'
              }`}>
                {level.status === 'Locked' ? <Lock className="w-6 h-6" /> : <Target className="w-6 h-6" />}
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium border ${
                level.difficulty === 'Easy' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                level.difficulty === 'Medium' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' :
                'bg-red-500/10 text-red-400 border-red-500/20'
              }`}>
                {level.difficulty}
              </div>
            </div>

            <h3 className="text-xl font-bold mb-1">{level.name}</h3>
            <p className="text-sm text-muted-foreground mb-6">{level.scenarios} Scenarios Configured</p>

            <div className="flex gap-2">
              <NeonButton className="flex-1" disabled={level.status === 'Locked'}>
                <Settings className="w-4 h-4 mr-2" /> Configure
              </NeonButton>
              {level.status === 'Active' && (
                <NeonButton variant="secondary" className="px-3">
                  <Play className="w-4 h-4" />
                </NeonButton>
              )}
            </div>
          </GlassCard>
        ))}

        <GlassCard className="border-dashed border-2 border-white/10 bg-transparent hover:bg-white/5 cursor-pointer flex flex-col items-center justify-center gap-3 min-h-[200px]">
          <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
            <Plus className="w-6 h-6 text-muted-foreground" />
          </div>
          <span className="font-medium text-muted-foreground">New Level</span>
        </GlassCard>
      </div>

      {/* Visual Scenario Editor Preview */}
      <GlassCard className="mt-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-bold text-lg">Scenario Editor Preview</h3>
          <NeonButton size="sm" variant="secondary">Open Full Editor</NeonButton>
        </div>
        
        <div className="p-8 bg-black/40 rounded-xl border border-white/5 flex justify-center">
           {/* This simulates the game card look */}
           <div className="w-full max-w-md bg-[#1a1a1e] border border-white/10 rounded-2xl p-6 shadow-2xl">
             <div className="mb-4">
               <span className="text-xs font-bold text-primary tracking-widest uppercase">Decision Point</span>
             </div>
             <h4 className="text-xl font-bold mb-6 leading-tight">
               A major corporation offers a donation to your campaign in exchange for lenient regulations.
             </h4>
             <div className="space-y-3">
               <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-sm hover:border-primary/50 cursor-pointer transition-colors">
                 Accept the donation quietly. (+Funds, -Integrity)
               </div>
               <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-sm hover:border-primary/50 cursor-pointer transition-colors">
                 Publicly reject the offer. (+Integrity, -Funds)
               </div>
             </div>
           </div>
        </div>
      </GlassCard>
    </div>
  );
};
